import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  PersonData,
  UniversalCheckResult,
  DataAnalysis,
  LiveReportStructure,
  AsyncTaskStatus,
  ContextModuleInfo,
} from "@/types/api";
import { pathForPage } from "@/utils/pageRoutes";

/**
 * Навигатор react-router, прокинутый из `App`. Живёт вне состояния стора:
 * это не данные, а мост в роутер, и в devtools ему делать нечего.
 *
 * Нужен потому, что `setCurrentPage` зовут из десятков мест вне компонентов
 * роутера, а менять он теперь должен адрес (адрес — источник истины).
 */
let routerNavigate: ((path: string) => void) | null = null;

/** Подключает/отключает мост в роутер (вызывается из `App`). */
export function setRouterNavigate(navigate: ((path: string) => void) | null): void {
  routerNavigate = navigate;
}

interface Notification {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message?: string;
  duration?: number;
}

export type ThemePreference = "system" | "light" | "dark";

/** Ключ явного выбора темы; тот же читает inline-скрипт в index.html. */
export const THEME_STORAGE_KEY = "context-theme";

/** Сохранённый выбор темы; без него — тёмная, основная тема продукта. */
function readStoredThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Хранилище недоступно (приватный режим) — остаемся на теме по умолчанию.
  }
  return "dark";
}

interface AppState {
  // UI состояние
  currentPage: string;
  sidebarCollapsed: boolean;
  /** Фактическая тема: следствие themePreference и системной настройки. */
  darkMode: boolean;
  /**
   * Выбор пользователя. «system» — следовать за устройством; явный выбор
   * сохраняется между сессиями и применяется до первого рендера
   * (inline-скрипт в index.html читает тот же ключ THEME_STORAGE_KEY).
   */
  themePreference: ThemePreference;
  loading: boolean;

  // Уведомления
  notifications: Notification[];

  // Данные
  personData: PersonData;
  currentCheck: UniversalCheckResult | null;
  dataAnalysis: DataAnalysis | null;
  liveSession: LiveReportStructure | null;
  asyncTasks: Record<string, AsyncTaskStatus>;
  systemInfo: ContextModuleInfo | null;
  currentReportJobId: string | null;

  // Данные ЮЛ
  legalEntityData: LegalEntityCheckData;
  currentLegalReportJobId: string | null;

  // Просмотр отчёта по последнему прогону подписки мониторинга
  monitoringReportSubscriptionId: string | null;

  // История
  checkHistory: UniversalCheckResult[];

  // Действия
  setCurrentPage: (page: string) => void;
  /**
   * Обратная сторона синхронизации: адрес → стор. Зовётся только из `App`
   * при смене location (включая «назад» и F5) и намеренно НЕ трогает роутер,
   * иначе получилась бы петля navigate ↔ setState.
   */
  setCurrentPageFromLocation: (page: string) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  /** Применяет вычисленную тему; вызывается из App при смене выбора или системной темы. */
  setDarkMode: (darkMode: boolean) => void;
  setThemePreference: (preference: ThemePreference) => void;
  setLoading: (loading: boolean) => void;

  // Уведомления
  addNotification: (notification: Omit<Notification, "id">) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Данные
  setPersonData: (data: PersonData) => void;
  updatePersonData: (data: Partial<PersonData>) => void;
  setCurrentCheck: (result: UniversalCheckResult | null) => void;
  setDataAnalysis: (analysis: DataAnalysis | null) => void;
  setLiveSession: (session: LiveReportStructure | null) => void;
  updateLiveSession: (updates: Partial<LiveReportStructure>) => void;
  updateAsyncTask: (taskId: string, status: AsyncTaskStatus) => void;
  setSystemInfo: (info: ContextModuleInfo) => void;
  setCurrentReportJobId: (jobId: string | null) => void;

  setLegalEntityData: (data: LegalEntityCheckData) => void;
  setCurrentLegalReportJobId: (jobId: string | null) => void;
  setMonitoringReportSubscriptionId: (subscriptionId: string | null) => void;

  // История
  addToHistory: (result: UniversalCheckResult) => void;
  clearHistory: () => void;

  // Сброс
  reset: () => void;
}

const initialPersonData: PersonData = {
  last_name: "",
  first_name: "",
  middle_name: "",
  birth_date: "",
  inn: "",
};

export interface LegalEntityCheckData {
  inn: string;
  ogrn: string;
}

const initialLegalEntityData: LegalEntityCheckData = {
  inn: "",
  ogrn: "",
};

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // Начальное состояние
      currentPage: "complex_check",
      sidebarCollapsed: false,
      darkMode: false,
      // Тёмная — основная тема продукта (как inforensic.pro). Явный выбор
      // читаем из того же ключа, что inline-скрипт index.html: иначе стор
      // стартовал со значения по умолчанию и перекрашивал страницу обратно.
      themePreference: readStoredThemePreference(),
      loading: false,

      notifications: [],

      personData: initialPersonData,
      currentCheck: null,
      dataAnalysis: null,
      liveSession: null,
      asyncTasks: {},
      systemInfo: null,
      currentReportJobId: null,

      legalEntityData: initialLegalEntityData,
      currentLegalReportJobId: null,

      monitoringReportSubscriptionId: null,

      checkHistory: [],

      // UI действия
      // setCurrentPage остаётся рабочим (его зовут из ~20 файлов), но теперь
      // он не «переключает экран», а меняет адрес: страницу выберет роутер,
      // а currentPage вернётся сюда через setCurrentPageFromLocation.
      // Состояние ставим и здесь — на случай вызова до подключения роутера
      // (ранний старт, тесты), чтобы поведение не зависело от гонки.
      setCurrentPage: (page) => {
        const { currentReportJobId, currentLegalReportJobId } = get();
        set({ currentPage: page });
        routerNavigate?.(
          pathForPage(page, {
            reportJobId: currentReportJobId,
            legalReportJobId: currentLegalReportJobId,
          }),
        );
      },

      setCurrentPageFromLocation: (page) => set({ currentPage: page }),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setDarkMode: (darkMode) => set({ darkMode }),
      setThemePreference: (preference) => {
        set({ themePreference: preference });
        // Отдельный ключ, а не JSON стора: его читает inline-скрипт в
        // index.html до загрузки бандла, чтобы страница не мигала светлой.
        try {
          // "system" хранится явно: отсутствие ключа теперь означает тёмную тему.
          localStorage.setItem(THEME_STORAGE_KEY, preference);
        } catch {
          // Приватный режим или запрет хранилища — выбор проживет до перезагрузки.
        }
      },
      setLoading: (loading) => set({ loading }),

      // Уведомления
      addNotification: (notification) => {
        const id = Math.random().toString(36).substr(2, 9);
        const newNotification = { ...notification, id };

        set((state) => ({
          notifications: [...state.notifications, newNotification],
        }));

        // Автоматическое удаление уведомления
        if (notification.duration !== 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, notification.duration || 5000);
        }
      },

      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      clearNotifications: () => set({ notifications: [] }),

      // Данные
      setPersonData: (data) => set({ personData: data }),

      updatePersonData: (data) =>
        set((state) => ({
          personData: { ...state.personData, ...data },
        })),

      setCurrentCheck: (result) => {
        set({ currentCheck: result });
        if (result) {
          get().addToHistory(result);
        }
      },

      setDataAnalysis: (analysis) => set({ dataAnalysis: analysis }),

      setLiveSession: (session) => set({ liveSession: session }),

      updateLiveSession: (updates) =>
        set((state) => ({
          liveSession: state.liveSession ? { ...state.liveSession, ...updates } : null,
        })),

      updateAsyncTask: (taskId, status) =>
        set((state) => ({
          asyncTasks: { ...state.asyncTasks, [taskId]: status },
        })),

      setSystemInfo: (info) => set({ systemInfo: info }),

      setCurrentReportJobId: (jobId) => set({ currentReportJobId: jobId }),

      setLegalEntityData: (data) => set({ legalEntityData: data }),
      setCurrentLegalReportJobId: (jobId) => set({ currentLegalReportJobId: jobId }),
      setMonitoringReportSubscriptionId: (subscriptionId) => set({ monitoringReportSubscriptionId: subscriptionId }),

      // История
      addToHistory: (result) =>
        set((state) => {
          const existingIndex = state.checkHistory.findIndex((item) => item.request_id === result.request_id);

          if (existingIndex >= 0) {
            // Обновляем существующую запись
            const updatedHistory = [...state.checkHistory];
            updatedHistory[existingIndex] = result;
            return { checkHistory: updatedHistory };
          } else {
            // Добавляем новую запись (максимум 50 записей)
            const newHistory = [result, ...state.checkHistory].slice(0, 50);
            return { checkHistory: newHistory };
          }
        }),

      clearHistory: () => set({ checkHistory: [] }),

      // Полный сброс
      reset: () =>
        set({
          currentPage: "complex_check",
          loading: false,
          personData: initialPersonData,
          currentCheck: null,
          dataAnalysis: null,
          liveSession: null,
          asyncTasks: {},
          notifications: [],
          currentReportJobId: null,
          legalEntityData: initialLegalEntityData,
          currentLegalReportJobId: null,
          monitoringReportSubscriptionId: null,
        }),
    }),
    {
      name: "compliance-app-store",
      partialize: (state: AppState) => ({
        // Сохраняем только UI настройки и историю. darkMode не сохраняется —
        // он выводится из themePreference и системной темы при старте.
        sidebarCollapsed: state.sidebarCollapsed,
        themePreference: state.themePreference,
        checkHistory: state.checkHistory,
      }),
    },
  ),
);

// Селекторы для удобства использования
export const useCurrentPage = () => useAppStore((state) => state.currentPage);
export const usePersonData = () => useAppStore((state) => state.personData);
export const useCurrentCheck = () => useAppStore((state) => state.currentCheck);
export const useDataAnalysis = () => useAppStore((state) => state.dataAnalysis);
export const useLiveSession = () => useAppStore((state) => state.liveSession);
export const useNotifications = () => useAppStore((state) => state.notifications);
export const useLoading = () => useAppStore((state) => state.loading);
export const useCheckHistory = () => useAppStore((state) => state.checkHistory);
export const useDarkMode = () => useAppStore((state) => state.darkMode);
export const useSidebarCollapsed = () => useAppStore((state) => state.sidebarCollapsed);
export const useCurrentReportJobId = () => useAppStore((state) => state.currentReportJobId);
export const useLegalEntityData = () => useAppStore((state) => state.legalEntityData);
export const useCurrentLegalReportJobId = () => useAppStore((state) => state.currentLegalReportJobId);

// Хуки для действий
export const useAppActions = () => {
  const store = useAppStore();
  return {
    setCurrentPage: store.setCurrentPage,
    setPersonData: store.setPersonData,
    updatePersonData: store.updatePersonData,
    setCurrentCheck: store.setCurrentCheck,
    setDataAnalysis: store.setDataAnalysis,
    setLiveSession: store.setLiveSession,
    updateLiveSession: store.updateLiveSession,
    addNotification: store.addNotification,
    removeNotification: store.removeNotification,
    setLoading: store.setLoading,
    reset: store.reset,
    setSidebarCollapsed: store.setSidebarCollapsed,
    setDarkMode: store.setDarkMode,
    setCurrentReportJobId: store.setCurrentReportJobId,
    setLegalEntityData: store.setLegalEntityData,
    setCurrentLegalReportJobId: store.setCurrentLegalReportJobId,
    setMonitoringReportSubscriptionId: store.setMonitoringReportSubscriptionId,
  };
};
